import { supabase } from './supabase';
import { TEAMS, TOTAL_STICKERS, getTeamStickerCodes } from '../data/stickers';
import {
  TEAMS_BY_CONFEDERATION,
  type Confederation,
} from '../data/teamConfederations';

interface SnapshotRow {
  user_id: string;
  event_type: string;
  payload: Record<string, unknown>;
  event_key: string;
}

const MILESTONES = [25, 50, 75, 90, 100] as const;

function teamComplete(counts: Record<string, number>, teamCode: string): boolean {
  const codes = getTeamStickerCodes(teamCode);
  return codes.every((c) => (counts[c] || 0) >= 1);
}

function ownedTotal(counts: Record<string, number>): number {
  let owned = 0;
  for (const team of TEAMS) {
    for (let i = 1; i <= 20; i++) {
      if ((counts[`${team.code}${i}`] || 0) >= 1) owned++;
    }
  }
  for (let i = 0; i <= 19; i++) {
    if ((counts[`FWC${i}`] || 0) >= 1) owned++;
  }
  return owned;
}

/**
 * Calcula marcos atuais do user e faz upsert em feed_events.
 * Idempotente via event_key — reset de figurinha não regera evento futuro.
 * Falha silenciosa.
 */
export async function recomputeFeedEvents(userId: string): Promise<void> {
  try {
    const { data: stickers } = await supabase
      .from('stickers')
      .select('sticker_code, count')
      .eq('user_id', userId);
    if (!stickers) return;

    const counts: Record<string, number> = {};
    for (const row of stickers as Array<{ sticker_code: string; count: number }>) {
      counts[row.sticker_code] = row.count;
    }

    const events: SnapshotRow[] = [];

    // 1) team_complete por seleção da Copa
    for (const team of TEAMS) {
      if (teamComplete(counts, team.code)) {
        events.push({
          user_id: userId,
          event_type: 'team_complete',
          payload: { team: team.code, team_name: team.name },
          event_key: `team_complete:${team.code}`,
        });
      }
    }

    // 2) milestone 25/50/75/90/100
    const owned = ownedTotal(counts);
    const pct = TOTAL_STICKERS > 0 ? Math.floor((owned / TOTAL_STICKERS) * 100) : 0;
    for (const m of MILESTONES) {
      if (pct >= m) {
        events.push({
          user_id: userId,
          event_type: 'milestone',
          payload: { percent: m },
          event_key: `milestone:${m}`,
        });
      }
    }

    // 3) confed_complete
    for (const confed of Object.keys(TEAMS_BY_CONFEDERATION) as Confederation[]) {
      if (TEAMS_BY_CONFEDERATION[confed].every((t) => teamComplete(counts, t))) {
        events.push({
          user_id: userId,
          event_type: 'confed_complete',
          payload: { confed },
          event_key: `confed_complete:${confed}`,
        });
      }
    }

    if (events.length === 0) return;

    const { error } = await supabase
      .from('feed_events')
      .upsert(events, { onConflict: 'user_id,event_key', ignoreDuplicates: true });

    if (error) console.error('recomputeFeedEvents upsert error', error);
  } catch (err) {
    console.error('recomputeFeedEvents failed', err);
  }
}

/**
 * Helper opcional: gera evento sticker_burst quando user adicionou >=5 figurinhas
 * em até 1h. Cooldown de 6h pelo event_key (`sticker_burst:YYYY-MM-DD-HH`).
 */
export async function maybeGenerateBurst(
  userId: string,
  recentAddCount: number
): Promise<void> {
  if (recentAddCount < 5) return;
  try {
    const now = new Date();
    const burstKey = `sticker_burst:${now.toISOString().slice(0, 13)}`; // YYYY-MM-DDTHH
    await supabase.from('feed_events').upsert(
      {
        user_id: userId,
        event_type: 'sticker_burst',
        payload: { count: recentAddCount },
        event_key: burstKey,
      },
      { onConflict: 'user_id,event_key', ignoreDuplicates: true }
    );
  } catch (err) {
    console.error('maybeGenerateBurst failed', err);
  }
}

/**
 * Disparado quando user passa uma figurinha de count=1 pra count=2 (virou repetida).
 * Pra cada amigo cuja wishlist contém essa figurinha, gera feed_event direcionado
 * (`audience_user_id = friendId`). Idempotente por dia via event_key.
 */
export async function maybeGenerateWishlistMatch(
  userId: string,
  stickerCode: string
): Promise<void> {
  try {
    // Acha amigos do user que têm essa figurinha na wishlist
    // Usa friendships pra filtrar (RLS na wishlist permite read se for amigo).
    const { data: friendships } = await supabase
      .from('friendships')
      .select('friend_id')
      .eq('user_id', userId);

    const friendIds = (friendships ?? []).map((r: { friend_id: string }) => r.friend_id);
    if (friendIds.length === 0) return;

    const { data: matches } = await supabase
      .from('wishlist')
      .select('user_id')
      .in('user_id', friendIds)
      .eq('sticker_code', stickerCode);

    if (!matches || matches.length === 0) return;

    const today = new Date().toISOString().slice(0, 10);
    const rows = (matches as Array<{ user_id: string }>).map((m) => ({
      user_id: userId,
      event_type: 'wishlist_match',
      payload: { sticker_code: stickerCode, friend_id: m.user_id },
      event_key: `wishlist_match:${m.user_id}:${stickerCode}:${today}`,
      audience_user_id: m.user_id,
    }));

    await supabase
      .from('feed_events')
      .upsert(rows, { onConflict: 'user_id,event_key', ignoreDuplicates: true });
  } catch (err) {
    console.error('maybeGenerateWishlistMatch failed', err);
  }
}

/**
 * Streak milestone (7/30/100). Idempotente por valor de `days`.
 */
export async function maybeGenerateStreakMilestone(
  userId: string,
  streakDays: number
): Promise<void> {
  const milestones = [7, 30, 100];
  const target = milestones.find((m) => streakDays === m);
  if (!target) return;
  try {
    await supabase.from('feed_events').upsert(
      {
        user_id: userId,
        event_type: 'streak_milestone',
        payload: { days: target },
        event_key: `streak:${target}`,
      },
      { onConflict: 'user_id,event_key', ignoreDuplicates: true }
    );
  } catch (err) {
    console.error('maybeGenerateStreakMilestone failed', err);
  }
}
